import { isContentEnrolled } from '@src/mappers/collection/track.mapper'
import { getPlayableEntityByQueryParams } from '@src/mappers/content/content-entity.mapper'
import { getRoutePrefixByContentType } from '@src/utils/url.util'

export type ContentWithSections = Pick<
  CocoApi.Program | CocoApi.Compilation | CocoApi.BODiBlock,
  '_type' | 'sections' | 'entities' | 'slug' | 'userData' | 'tracks'
>

type ContentWithSectionEntities = Pick<
  ContentWithSections,
  'sections' | 'entities' | 'userData' | 'tracks'
>

export const isCdpContent = (value: unknown): value is ContentWithSections =>
  value != null &&
  typeof value === 'object' &&
  'sections' in value &&
  '_type' in value &&
  'slug' in value &&
  !('status' in value && (value as { status?: string }).status === 'error')

const moduleContainsEntity = (module: CocoApi.Module, entityId: string): boolean => {
  if ('entityIds' in module && module.entityIds?.includes(entityId)) {
    return true
  }

  if (module._type === 'dynamicModule' && module.slug === 'overview') {
    return module.media?._id === entityId
  }

  return false
}

const getSectionSlugForEntity = (
  content: Pick<ContentWithSectionEntities, 'sections'>,
  entityId?: string
): string | undefined => {
  if (!entityId) return undefined

  for (const section of content.sections ?? []) {
    const containsEntity = section.modules?.some((module) => moduleContainsEntity(module, entityId))

    if (containsEntity && section.slug) {
      return section.slug
    }
  }

  return undefined
}

export const resolveContentDetailsSectionSlug = (
  content: ContentWithSectionEntities,
  videoGuid: string | null | undefined,
  videoSlug: string | null | undefined
): string | undefined => {
  const firstSectionSlug = content.sections?.[0]?.slug

  if (!videoGuid && !videoSlug) {
    // Workouts is the only view that scrolls to the up-next workout.
    if (isContentEnrolled(content)) {
      const workoutsSlug = content.sections?.find((s) => s.slug === 'workouts')?.slug
      if (workoutsSlug) return workoutsSlug
    }
    return firstSectionSlug
  }

  const entity = getPlayableEntityByQueryParams(content, videoGuid ?? null, videoSlug ?? null)
  if (!entity?._id) {
    return firstSectionSlug
  }

  return getSectionSlugForEntity(content, entity._id) ?? firstSectionSlug
}

export const buildContentDetailsSectionRedirectPath = ({
  country,
  lang,
  content,
  videoGuid,
  videoSlug,
  search,
}: {
  country: string
  lang: string
  content: ContentWithSections
  videoGuid: string | null
  videoSlug: string | null
  search: string
}): string | undefined => {
  const sectionSlug = resolveContentDetailsSectionSlug(content, videoGuid, videoSlug)
  if (!sectionSlug || !content.slug) return undefined

  const routePrefix = getRoutePrefixByContentType(content._type)
  const basePath = `/app/${country}/${lang}/${routePrefix}/${content.slug}/${sectionSlug}`

  return search ? `${basePath}${search}` : basePath
}
